/**
 * Speakers, groups and the now-playing selection for one home, derived from
 * the live store. Group membership comes from HA's `group_members` attribute
 * (coordinator first). Controls go through store.call (the action gateway)
 * with optimistic overlays so taps feel instant.
 */
import { useCallback, useMemo, useSyncExternalStore } from 'react';
import { coordinatorOf, pickNowPlaying, type EntityMeta, type EntityState } from '@home/shared';
import { useAccess, useDerived, useModel, useTicker } from './hooks.js';
import { store } from './store.js';
import { num, str, strList } from './entity.js';

export interface Speaker {
  id: string;
  name: string;
  roomName: string | null;
  floorId: string | null;
  floorName: string | null;
}

const isSpeaker = (m: EntityMeta): boolean => m.domain === 'media_player' && m.platform === 'sonos' && m.category === null;
const isPlayer = (m: EntityMeta): boolean => m.domain === 'media_player' && m.category === null;

/** Visible Sonos speakers, ordered by floor then room then name. */
export function useSpeakers(home: string): Speaker[] {
  const model = useModel(home);
  const access = useAccess(home);
  return useMemo(() => {
    if (!model || !access) return [];
    const rooms = new Map(model.rooms.map((r) => [r.id, r]));
    const floors = new Map(model.floors.map((f) => [f.id, f]));
    return Object.values(model.entities)
      .filter((m) => isSpeaker(m) && access.entities[m.id])
      .map((m) => {
        const roomId = m.roomId ?? (m.deviceId ? model.devices[m.deviceId]?.roomId ?? null : null);
        const room = roomId ? rooms.get(roomId) : undefined;
        const floor = room?.floorId ? floors.get(room.floorId) : undefined;
        return { id: m.id, name: m.name, roomName: room?.name ?? null, floorId: floor?.id ?? null, floorName: floor?.name ?? null, order: [floor?.order ?? 99, room?.order ?? 99] as const };
      })
      .sort((a, b) => a.order[0] - b.order[0] || a.order[1] - b.order[1] || a.name.localeCompare(b.name))
      .map(({ order: _o, ...s }) => s);
  }, [model, access]);
}

export const groupMembers = (id: string, st: EntityState | undefined): string[] => {
  const m = strList(st?.a['group_members']);
  return m.length ? m : [id];
};
export const coordinator = (id: string, st: EntityState | undefined): string => coordinatorOf({ id, members: groupMembers(id, st) });
export const isUnavailableState = (st: EntityState | undefined): boolean => !st || st.s === 'unavailable' || st.s === 'unknown';

/** Live states of many entities, re-rendering only when one of them changes. */
export function useStates(home: string, ids: readonly string[]): Map<string, EntityState | undefined> {
  return useDerived(home, ids, () => new Map(ids.map((id) => [id, store.entity(home, id)])));
}

// ------------------------------------------------------------------ now playing choice

const choice = new Map<string, { preferred: string | null; sticky: string | null }>();
const choiceSubs = new Set<() => void>();
let choiceVersion = 0;
const setChoice = (home: string, patch: Partial<{ preferred: string | null; sticky: string | null }>): void => {
  const cur = choice.get(home) ?? { preferred: null, sticky: null };
  const next = { ...cur, ...patch };
  if (next.preferred === cur.preferred && next.sticky === cur.sticky) return;
  choice.set(home, next);
  choiceVersion++;
  choiceSubs.forEach((f) => f());
};

/** Paused/buffering players drop out of the bar this long after they stopped (seconds). */
export const NOW_PLAYING_STALE_S = 10 * 60;

/**
 * Whether a player counts as real audio playback for the now-playing bar. TVs (device_class tv) and a
 * soundbar on its TV input (Sonos "TV" / HDMI stream, which HA reports as `playing` whenever the TV is on,
 * even idle at a menu) never do; a paused or buffering player only within NOW_PLAYING_STALE_S of pausing.
 */
export function isAudioPlayback(meta: Pick<EntityMeta, 'deviceClass'> | undefined, st: EntityState | undefined, nowS = Date.now() / 1000): boolean {
  if (!st) return false;
  if (meta?.deviceClass === 'tv' || st.a['device_class'] === 'tv') return false;
  const source = str(st.a['source'])?.toLowerCase();
  const content = str(st.a['media_content_id']) ?? '';
  if (source === 'tv' || source === 'hdmi' || /^x-sonos-htastream:|^x-sonos-vli:/.test(content)) return false;
  if (st.s === 'playing') return true;
  if (st.s === 'paused' || st.s === 'buffering') return nowS - (st.lc ?? 0) < NOW_PLAYING_STALE_S;
  return false;
}

/** What the now-playing bar shows: the most recently started player (or the switcher's choice). */
export function useNowPlaying(home: string): { current: string | null; all: string[]; choose: (id: string) => void } {
  const model = useModel(home);
  const access = useAccess(home);
  const ids = useMemo(
    () => (model && access ? Object.values(model.entities).filter((m) => isPlayer(m) && m.deviceClass !== 'tv' && access.entities[m.id]).map((m) => m.id) : []),
    [model, access],
  );
  useSyncExternalStore((f) => (choiceSubs.add(f), () => choiceSubs.delete(f)), () => choiceVersion, () => choiceVersion);
  const c = choice.get(home) ?? { preferred: null, sticky: null };
  // Re-evaluate each minute so a long-paused player leaves the bar without needing a state change.
  const minute = Math.floor(useTicker(60_000) / 60_000);
  const result = useDerived(home, [...ids, `#${c.preferred}|${c.sticky}|${minute}`], () => {
    const nowS = Date.now() / 1000;
    const players = ids.map((id) => {
      const st = store.entity(home, id);
      // Not audio (TV input, stale pause): reported as idle so it never becomes a candidate.
      const state = isAudioPlayback(model?.entities[id], st, nowS) ? (st?.s ?? 'unavailable') : 'idle';
      return { id, state, lc: st?.lc ?? 0, members: groupMembers(id, st) };
    });
    return pickNowPlaying(players, c);
  });
  // Keep showing the current player while it's paused (so pausing from the bar doesn't hide it).
  if (result.current !== c.sticky) queueMicrotask(() => setChoice(home, { sticky: result.current }));
  const choose = useCallback((id: string) => setChoice(home, { preferred: id, sticky: id }), [home]);
  return { ...result, choose };
}

// ------------------------------------------------------------------ media info

export interface MediaInfo {
  state: string;
  playing: boolean;
  title: string | null;
  artist: string | null;
  album: string | null;
  art: string | null;
  duration: number | null;
  position: number | null;
  positionAt: number | null;
  volume: number | null;
  muted: boolean;
  shuffle: boolean | null;
  repeat: 'off' | 'all' | 'one' | null;
  source: string | null;
}

export function mediaInfo(st: EntityState | undefined): MediaInfo {
  const a = st?.a ?? {};
  const at = str(a['media_position_updated_at']);
  const rep = str(a['repeat']);
  return {
    state: st?.s ?? 'unavailable',
    playing: st?.s === 'playing',
    title: str(a['media_title']) ?? null,
    artist: str(a['media_artist']) ?? str(a['media_album_artist']) ?? null,
    album: str(a['media_album_name']) ?? null,
    art: str(a['entity_picture']) ?? null,
    duration: num(a['media_duration']) ?? null,
    position: num(a['media_position']) ?? null,
    positionAt: at ? Date.parse(at) : null,
    volume: num(a['volume_level']) ?? null,
    muted: a['is_volume_muted'] === true,
    shuffle: typeof a['shuffle'] === 'boolean' ? a['shuffle'] : null,
    repeat: rep === 'off' || rep === 'all' || rep === 'one' ? rep : null,
    source: str(a['source']) ?? null,
  };
}

/** Current playback position in seconds (extrapolated while playing). */
export function livePosition(m: MediaInfo, now = Date.now()): number | null {
  if (m.position === null) return null;
  const p = m.playing && m.positionAt ? m.position + (now - m.positionAt) / 1000 : m.position;
  return m.duration ? Math.min(m.duration, Math.max(0, p)) : Math.max(0, p);
}

// ------------------------------------------------------------------ actions

const quiet = <T>(p: Promise<T>): void => void p.catch(() => undefined);
const patchA = (a: Record<string, unknown>) => (cur: EntityState): EntityState => ({ ...cur, a: { ...cur.a, ...a } });

export const media = {
  playPause(home: string, id: string, playing: boolean): void {
    quiet(store.call(home, 'media_player', playing ? 'media_pause' : 'media_play', [id], undefined, { optimistic: (c) => ({ ...c, s: playing ? 'paused' : 'playing' }) }));
  },
  next(home: string, id: string): void {
    quiet(store.call(home, 'media_player', 'media_next_track', [id]));
  },
  previous(home: string, id: string): void {
    quiet(store.call(home, 'media_player', 'media_previous_track', [id]));
  },
  seek(home: string, id: string, seconds: number): void {
    quiet(store.call(home, 'media_player', 'media_seek', [id], { seek_position: Math.round(seconds) }, {
      optimistic: patchA({ media_position: Math.round(seconds), media_position_updated_at: new Date().toISOString() }),
    }));
  },
  shuffle(home: string, id: string, on: boolean): void {
    quiet(store.call(home, 'media_player', 'shuffle_set', [id], { shuffle: on }, { optimistic: patchA({ shuffle: on }) }));
  },
  repeat(home: string, id: string, mode: 'off' | 'all' | 'one'): void {
    quiet(store.call(home, 'media_player', 'repeat_set', [id], { repeat: mode }, { optimistic: patchA({ repeat: mode }) }));
  },
  volume(home: string, id: string, v: number): void {
    quiet(store.call(home, 'media_player', 'volume_set', [id], { volume_level: v }, { optimistic: patchA({ volume_level: v }) }));
  },
  mute(home: string, id: string, muted: boolean): void {
    quiet(store.call(home, 'media_player', 'volume_mute', [id], { is_volume_muted: muted }, { optimistic: patchA({ is_volume_muted: muted }) }));
  },
  /** Adds `ids` to `coord`'s group. */
  join(home: string, coord: string, ids: string[]): Promise<unknown> {
    const cur = groupMembers(coord, store.entity(home, coord));
    const members = [...new Set([...cur, ...ids])];
    return store.call(home, 'media_player', 'join', [coord], { group_members: ids }, { optimistic: patchA({ group_members: members }) });
  },
  unjoin(home: string, id: string): Promise<unknown> {
    return store.call(home, 'media_player', 'unjoin', [id], undefined, { optimistic: patchA({ group_members: [id] }) });
  },
  /** Makes the group exactly `ids` (first = coordinator if it isn't grouped elsewhere). */
  async setGroup(home: string, ids: string[]): Promise<void> {
    if (!ids.length) return;
    const coord = ids[0]!;
    const current = groupMembers(coord, store.entity(home, coord));
    const leave = current.filter((m) => !ids.includes(m));
    const add = ids.filter((m) => m !== coord && !current.includes(m));
    await Promise.all(leave.map((m) => media.unjoin(home, m).catch(() => undefined)));
    if (add.length) await media.join(home, coord, add).catch(() => undefined);
  },
};
